import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { showErrorToast } from "../../components/Toast";
import { groupNotifications } from "./groupNotifications";
import { routeForTarget } from "./notificationRouting";
import {
  useGetNotificationsQuery,
  useMarkReadMutation,
} from "./notificationsApi";
import useUnreadNotifications from "./useUnreadNotifications";

const PAGE_STEP = 20;
const MARK_ALL_CHUNK = 8;

const chunk = (list, size) => {
  const chunks = [];

  for (let index = 0; index < list.length; index += size) {
    chunks.push(list.slice(index, index + size));
  }

  return chunks;
};

export const useNotificationsInbox = ({ enabled = true, onNavigate } = {}) => {
  const navigate = useNavigate();

  const [size, setSize] = useState(PAGE_STEP);
  const [paginating, setPaginating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);

  const mounted = useRef(true);

  const { data, currentData, isFetching, isError, error, refetch } =
    useGetNotificationsQuery({ size }, { skip: !enabled });
  const [markRead] = useMarkReadMutation();
  const badge = useUnreadNotifications();

  const page = currentData ?? (paginating ? data : undefined);

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  useEffect(() => {
    if (!isFetching) {
      setPaginating(false);
      setRefreshing(false);
    }
  }, [isFetching]);

  const refresh = useCallback(() => {
    setRefreshing(true);
    refetch();
    badge.refetch();
  }, [refetch, badge]);

  const loadMore = useCallback(() => {
    if (page?.isLast || isFetching || isError) return;

    setPaginating(true);
    setSize((previous) => previous + PAGE_STEP);
  }, [page, isFetching, isError]);

  const sections = useMemo(() => groupNotifications(page?.items), [page]);

  const loadedUnread = useMemo(
    () => (page?.items || []).filter((item) => !item.read),
    [page]
  );

  const open = useCallback(
    async (item) => {
      const route = routeForTarget(item.target);
      const go = () => {
        if (!route) return;

        onNavigate?.();
        navigate(route.path, { state: route.state });
      };

      if (item.read) return go();
      if (markingId) return undefined;

      setMarkingId(item.id);

      try {
        await markRead(item.id).unwrap();
      } catch {
        showErrorToast("Could not open this notification");
      } finally {
        if (mounted.current) setMarkingId(null);
      }

      return go();
    },
    [markingId, markRead, navigate, onNavigate]
  );

  const markAllRead = useCallback(async () => {
    if (markingAll || !loadedUnread.length) return;

    setMarkingAll(true);

    let failed = false;

    for (const group of chunk(loadedUnread, MARK_ALL_CHUNK)) {
      // eslint-disable-next-line no-await-in-loop
      const results = await Promise.allSettled(
        group.map((item) => markRead(item.id).unwrap())
      );

      if (results.some((result) => result.status === "rejected")) failed = true;
    }

    if (failed) {
      showErrorToast("Could not mark every notification as read");
      badge.refetch();
    }

    if (mounted.current) setMarkingAll(false);
  }, [markingAll, loadedUnread, markRead, badge]);

  const unreadCount = badge.hasUnread ? badge.count : loadedUnread.length;

  return {
    sections,
    unreadCount,
    unreadLabel: unreadCount > 99 ? "99+" : String(unreadCount),
    isLast: page?.isLast ?? true,
    showSkeleton: enabled && !page && !paginating,
    paginating: isFetching && paginating,
    refreshing,
    markingId,
    markingAll,
    hasRows: Boolean(page?.items?.length),
    errorKind: isError && !page ? error?.kind || "network" : null,
    blockedByError: isError && Boolean(page),
    refresh,
    loadMore,
    open,
    markAllRead,
  };
};

export default useNotificationsInbox;
